import {
  buildLocalePath,
  getLocaleFromPath,
  getPathSuffixWithoutLocale,
  isSupportedLocale,
  normalizePathname,
  SUPPORTED_LOCALES,
  type MultilingualLocale,
} from "./localePath"

export { SUPPORTED_LOCALES, type MultilingualLocale } from "./localePath"

const HREFLANG_BY_LOCALE: Record<MultilingualLocale, string> = {
  en: "en",
  th: "th",
  id: "id",
  vn: "vi-VN",
  fr: "fr",
  ru: "ru",
  pt: "pt",
  es: "es",
  ko: "ko",
}

export interface AlternateLink {
  locale: MultilingualLocale
  hreflang: string
  href: string
}

export interface MultilingualSeoLinks {
  canonical: string
  alternates: AlternateLink[]
  xDefault: string
  currentLocale: MultilingualLocale
}

export interface BuildMultilingualSeoLinksInput {
  pathname: string
  host?: string | null
  protocol?: string | null
  currentLocale?: string
  defaultLocale?: MultilingualLocale
}

function buildOrigin(host?: string | null, protocol?: string | null): string {
  const safeHost = (host || "www.metal.com").split(",")[0]?.trim() || "www.metal.com"
  const rawProtocol = (protocol || "https").split(",")[0]?.trim().toLowerCase() || "https"
  const safeProtocol = rawProtocol === "http" || rawProtocol === "https" ? rawProtocol : "https"
  const isLocalHost = /^(localhost|127\.0\.0\.1|\[::1\])(?::\d+)?$/i.test(safeHost)
  const finalProtocol = isLocalHost ? safeProtocol : "https"

  return `${finalProtocol}://${safeHost}`
}

export function buildMultilingualSeoLinks(input: BuildMultilingualSeoLinksInput): MultilingualSeoLinks {
  const normalizedPathname = normalizePathname(input.pathname || "/")
  const localeFromPath = getLocaleFromPath(normalizedPathname)
  const defaultLocale = isSupportedLocale(input.defaultLocale) ? input.defaultLocale : "en"
  const currentLocale = isSupportedLocale(input.currentLocale) ? input.currentLocale : localeFromPath || defaultLocale
  const suffix = getPathSuffixWithoutLocale(normalizedPathname)
  const origin = buildOrigin(input.host, input.protocol)

  const alternates = SUPPORTED_LOCALES.map((locale) => ({
    locale,
    hreflang: HREFLANG_BY_LOCALE[locale],
    href: `${origin}${buildLocalePath(locale, suffix, { omitDefaultLocalePrefix: true })}`,
  }))

  return {
    canonical: `${origin}${buildLocalePath(currentLocale, suffix, { omitDefaultLocalePrefix: true })}`,
    alternates,
    xDefault: `${origin}${buildLocalePath("en", suffix, { omitDefaultLocalePrefix: true })}`,
    currentLocale,
  }
}
